<template>
    <div>
        <v-row>
            <v-col>
                <h4 class="text-h4">Credit Note</h4>
                <p class="text-subtitle"> Credit Note Display</p>
                <v-divider></v-divider>
            </v-col>
        </v-row>
        <div class="text-center d-flex flex-column justify-center align-center pa-2" style="height: 100%" v-if="loading.data" >
            <v-progress-linear
                height="4"
                indeterminate>
            </v-progress-linear>
            <div class="text-body-1 pa-2">Please Wait ...</div>
        </div>
        <div
            v-else>
            <v-row>
                <v-col cols="12" md="3">
                    <v-btn
                        block
                        color="primary"
                        @click="simulate"
                    >
                        <v-icon left>mdi-form-select</v-icon>
                        Simulate
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3">
                    <v-btn
                        block
                        outlined
                        disabled
                    >
                       <v-icon left> mdi-printer</v-icon>
                       Print
                    </v-btn>
                </v-col>
            </v-row>
            <div>
                <v-alert text type="info" v-if="loading.simulate" dense v-html="aicaSimulatingAlert"></v-alert>
            </div>
             <v-row>
                <v-col cols="12" md="6">
                    <v-subheader>Generate Information</v-subheader>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Code</th>
                                <td>{{ data.code }}</td>
                            </tr>
                            <tr>
                                <th>Date</th>
                                <td>
                                    {{ formatTimestamp(data.created_at) }}
                                </td>
                            </tr>
                            <tr>
                                <th>Document Date</th>
                                <td>{{ data.date }}</td>
                            </tr>
                            <tr>
                                <th>State</th>
                                <td>
                                    <p-status :state="data.state"></p-status>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col cols="12" md="6">
                    <v-subheader>Customer Information</v-subheader>
                    <document-customer-preview :id="data.customer_id" v-if="data.customer_id"></document-customer-preview>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-simple-table dense class="text-body-1">
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ data.notes }}</td>
                                <td>{{ formatNumber(data.amount, 2) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row class="mt-2">
                <v-col cols="12">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>Created By</th>
                                <td>{{data.created_by}}</td>
                                <th>Created At</th>
                                <td>{{formatTimestamp(data.created_at)}}</td>
                            </tr>         
                            <tr>
                                <th>Updated By</th>
                                <td>{{data.updated_by}}</td>
                                <th>Updated At</th>
                                <td>{{formatTimestamp(data.updated_at)}}</td>
                            </tr>   
                            <tr v-if="isPosted">
                                <th>Posted By</th>
                                <td>{{data.posted_by}}</td>
                                <th>Posted At</th>
                                <td>{{formatTimestamp(data.posted_at)}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
        </div>
        <!-- TODO: can post to be changed with the real permissions -->
        <simulate-entries
            v-if="simulateEntryShown" 
            v-model="simulateEntryShown" 
            :entry="entry" 
            :is-posted="isPosted" 
            :can-post="true"
            @post="post"
            :is-posting="loading.post">
        </simulate-entries>
        <AlertDialog 
            ref="alertDialog"/>
    </div>
</template>

<script>
import moment from "moment";
import { formatNumber } from 'accounting';

import { aicaSimulatingAlert, aicaPostedAlert } from "../../assets/text/alerts";
import { getDpk } from "../../services/sales";
import { getCreditNote, postCreditNote, simulateCreditNote } from "../../services/sales/creditNote";

import DocumentCustomerPreview from "../../components/Sales/DocumentCustomerPreview";
import SimulateEntries from "../../components/accounting/SimulateEntries.vue";
import PStatus from '../../components/common/PStatus';
import AlertDialog from '@/components/common/AlertDialog';

// import LoadingDialog

export default {
    name: "CreditNoteDisplayView",
    components: { DocumentCustomerPreview, SimulateEntries, PStatus, AlertDialog },
    data() {
        return {
            data: {},
            loading: {
                data: false,
                simulate: false,
                post: false
            },
            id: null,
            entry: {},
            simulateEntryShown: false,
            aicaSimulatingAlert,
            aicaPostedAlert,
            dpk: null,
            show: true
        };
    },
    computed: {
        isPosted() {
            return this.data.is_posted;
        }
    },
    methods: {
        formatNumber,
        formatTimestamp(timestamp) {
            return moment(timestamp, true).format("YYYY-MM-DD");
        },
        fetchCreditNote() {
            this.loading.data = true;
            getCreditNote(this.id)
                .then((response) => {
                    this.data = response.data;
                })
                .finally(() => (this.loading.data = false));
        },
        simulate() {
            this.loading.simulate = true;
            simulateCreditNote({ creditNoteId: this.data.id })
                .then((response) => {
                    this.entry = response.data;
                    this.simulateEntryShown = true;
                }).catch((error) => {
                    this.loading.simulate = false;
                    let message = error.response ? error.response.data.message : error;
                    let errors = error.response ? error.response.data.errors : [];
                    message += '\n';
                    message += errors.join();
                    this.$refs.alertDialog.open({type: "error", message});
                })
                .finally(() => (this.loading.simulate = false));
        },
        post() {
            this.loading.post = true;
            postCreditNote({ creditNoteId: this.data.id, dpk: this.dpk })
                .then(response => {
                    this.data = response.data.record;
                    this.simulateEntryShown = false;
                    this.$emit("updated");
                }).finally(() => {
                    this.loading.post = false;
                });
        },
        fetchDpk() {
            getDpk().then((response) => (this.dpk = response.data.key));
        },
    },
    created() {
        this.id = this.$route.params.id
        this.fetchCreditNote();
        this.fetchDpk();
    }
};
</script>
